// Conversational intake for Analyst lead search.
//
// One freeform sentence in. Follow-up questions ONLY for slots the sentence
// left empty, in cost order, capped at three. Anything the Analyst had to
// expand or infer is shown back as editable chips BEFORE any credits are
// spent, because that expansion is the part most likely to be wrong.

import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Send, X, Plus, Sparkles, RotateCcw } from "lucide-react";
import { withCsrfHeader } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

const PLACEHOLDER =
  "find the marketing director or HR contact for all car dealerships in austin, TX";

type SlotConfidence = "confident" | "ambiguous" | "empty";

export type SearchSlots = {
  businessType: { raw: string | null; keywords: string[]; confidence: SlotConfidence; placesMappable: boolean };
  geography: {
    anchorKind: "point" | "city" | "state" | "cities" | "national" | null;
    anchorValue: string | null; cities: string[]; radiusMiles: number | null;
    confidence: SlotConfidence; notImportant: boolean;
  };
  role: { raw: string | null; titles: string[]; confidence: SlotConfidence; notImportant: boolean };
  fitCriteria: string | null;
  sizeHint: string | null;
  exclusions: string[];
  volumeTarget: number | null;
};

type IntakeQuestion = { slot: string; question: string; hint: string };
type Turn = { role: "user" | "assistant"; text: string; hint?: string };

/** Editable chip row. The whole point of the confirm step is that these are
 *  changeable — a read-only summary would just be a slower way to be wrong. */
function ChipRow({
  label, values, onChange, testId,
}: { label: string; values: string[]; onChange: (v: string[]) => void; testId: string }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  function commit() {
    const t = draft.trim();
    if (t && !values.some((v) => v.toLowerCase() === t.toLowerCase())) onChange([...values, t]);
    setDraft("");
    setAdding(false);
  }

  return (
    <div className="space-y-1.5">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {values.map((v) => (
          <Badge key={v} variant="secondary" className="gap-1 pr-1 font-normal" data-testid={`${testId}-chip`}>
            {v}
            <button
              type="button"
              onClick={() => onChange(values.filter((x) => x !== v))}
              className="rounded-sm hover:bg-muted-foreground/20 p-0.5"
              aria-label={`Remove ${v}`}
            >
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
        {adding ? (
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commit(); }
              if (e.key === "Escape") { setDraft(""); setAdding(false); }
            }}
            className="h-6 w-40 text-xs"
            data-testid={`${testId}-add-input`}
          />
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1 rounded-md border border-dashed px-2 py-0.5 text-[11px] text-muted-foreground hover:text-foreground hover:border-solid"
            data-testid={`${testId}-add`}
          >
            <Plus className="w-3 h-3" /> add
          </button>
        )}
      </div>
    </div>
  );
}

function geoSummary(g: SearchSlots["geography"]): string {
  if (g.notImportant || g.anchorKind === "national") return "Nationwide";
  if (g.anchorKind === "cities") return g.cities.join(", ") || "—";
  if (g.anchorKind === "point" && g.anchorValue) {
    return `Within ${g.radiusMiles ?? 25} miles of ${g.anchorValue}`;
  }
  return g.anchorValue || "—";
}

export function AnalystIntake({
  workspacePrefix,
  resultCap,
  running,
  onRun,
}: {
  workspacePrefix: string;
  resultCap: number;
  running: boolean;
  onRun: (request: any) => void;
}) {
  const { toast } = useToast();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState("");
  const [slots, setSlots] = useState<SearchSlots | null>(null);
  const [askedCount, setAskedCount] = useState(0);
  const [question, setQuestion] = useState<IntakeQuestion | null>(null);
  const [request, setRequest] = useState<any>(null);
  const [leadMode, setLeadMode] = useState<"place" | "role">("place");
  const [thinking, setThinking] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [turns, thinking, request]);

  function reset() {
    setTurns([]); setInput(""); setSlots(null); setAskedCount(0);
    setQuestion(null); setRequest(null); setLeadMode("place");
  }

  async function send() {
    const message = input.trim();
    if (!message || thinking) return;
    setInput("");
    setTurns((t) => [...t, { role: "user", text: message }]);
    setThinking(true);
    try {
      const headers = await withCsrfHeader({ "Content-Type": "application/json" });
      const res = await fetch(`${workspacePrefix}/api/lead-gen/ai/intake`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ message, slots, askedCount, resultCap }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).message || "Intake failed");
      }
      const data = await res.json();
      setSlots(data.slots);
      setLeadMode(data.leadMode || "place");
      setQuestion(data.question || null);
      setRequest(data.request || null);
      if (data.question) {
        setAskedCount((n) => n + 1);
        setTurns((t) => [...t, { role: "assistant", text: data.question.question, hint: data.question.hint }]);
      }
    } catch (e: any) {
      toast({ title: "Couldn't read that", description: e.message, variant: "destructive" });
      // Put the message back so the user doesn't have to retype it.
      setInput(message);
      setTurns((t) => t.slice(0, -1));
    } finally {
      setThinking(false);
    }
  }

  // Chip edits rewrite the compiled request too, otherwise the user would edit
  // the summary and watch the run ignore them.
  function patchSlots(next: SearchSlots) {
    setSlots(next);
    if (!request) return;
    setRequest({
      ...request,
      filters: {
        ...request.filters,
        venueTypes: next.businessType.keywords,
        targetContact: next.role.titles.join(", "),
        // Every editable chip row must map back into the request. Anything
        // missing here is a control the user can move that silently does
        // nothing to the run.
        exclude: next.exclusions,
      },
    });
  }

  const ready = !!request && !question;

  return (
    <div className="border rounded-lg bg-muted/20" data-testid="analyst-intake">
      <div ref={scrollRef} className="max-h-72 overflow-y-auto p-4 space-y-3">
        {turns.length === 0 && (
          <div className="flex items-start gap-2 text-sm text-muted-foreground">
            <Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
            <p>
              Describe who you're looking for in your own words. I'll ask only about
              what's missing — never more than three questions.
            </p>
          </div>
        )}

        {turns.map((t, i) => (
          <div key={i} className={t.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div
              className={
                t.role === "user"
                  ? "max-w-[85%] rounded-lg bg-primary text-primary-foreground px-3 py-2 text-sm"
                  : "max-w-[85%] rounded-lg bg-background border px-3 py-2 text-sm"
              }
              data-testid={`intake-turn-${t.role}`}
            >
              <p>{t.text}</p>
              {t.hint && <p className="mt-1 text-xs text-muted-foreground">{t.hint}</p>}
            </div>
          </div>
        ))}

        {thinking && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading that…
          </div>
        )}

        {ready && slots && (
          <div className="rounded-lg border bg-background p-3 space-y-3" data-testid="intake-confirm">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Here's what I'll search for</p>
              {leadMode === "role" && (
                <Badge variant="outline" className="text-[10px] font-normal">
                  role-led
                </Badge>
              )}
            </div>

            <ChipRow
              label="Business types"
              values={slots.businessType.keywords}
              onChange={(v) => patchSlots({ ...slots, businessType: { ...slots.businessType, keywords: v } })}
              testId="intake-business"
            />

            <div className="space-y-1.5">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Where</p>
              <p className="text-sm">{geoSummary(slots.geography)}</p>
            </div>

            {slots.role.notImportant ? (
              <div className="space-y-1.5">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Contact</p>
                <p className="text-sm text-muted-foreground">
                  Not needed — I'll skip contact lookup, which makes this much cheaper.
                </p>
              </div>
            ) : (
              <ChipRow
                label="Job titles"
                values={slots.role.titles}
                onChange={(v) => patchSlots({ ...slots, role: { ...slots.role, titles: v } })}
                testId="intake-role"
              />
            )}

            {slots.fitCriteria && (
              <div className="space-y-1">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Scoring for</p>
                <p className="text-sm text-muted-foreground">{slots.fitCriteria}</p>
              </div>
            )}

            {slots.exclusions.length > 0 && (
              <ChipRow
                label="Excluding"
                values={slots.exclusions}
                onChange={(v) => patchSlots({ ...slots, exclusions: v })}
                testId="intake-exclude"
              />
            )}

            <div className="flex items-center gap-2 pt-1">
              <Button
                size="sm"
                disabled={running}
                onClick={() => onRun(request)}
                data-testid="button-intake-run"
              >
                {running ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                Start search
              </Button>
              <Button size="sm" variant="ghost" onClick={reset} disabled={running} data-testid="button-intake-reset">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Start over
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="border-t p-2 flex items-center gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={question ? question.question : PLACEHOLDER}
          disabled={thinking || running}
          className="text-sm border-0 shadow-none focus-visible:ring-0"
          data-testid="input-intake"
        />
        <Button size="sm" onClick={send} disabled={thinking || running || !input.trim()} data-testid="button-intake-send">
          <Send className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}
